/**
 * The day's note. FR-208.
 *
 * Debounced, and flushed on blur and on unmount. The flush is the part that
 * matters: this control lives in a sheet that can be closed mid-sentence, and a
 * debounce without a flush silently discards whatever was typed inside the last
 * window. Losing a user's own words is the least acceptable bug this component
 * could have.
 *
 * ## No prop-syncing
 *
 * There is deliberately no "adopt the new value when the day changes" logic
 * here. The caller passes `key={date}`, so moving to another day remounts the
 * component: the unmount flush commits the previous day's text and the new
 * instance starts from that day's value. An earlier version synced props to
 * state through refs during render, which is both a React rule violation and a
 * race — the flush could land on the wrong day.
 *
 * Markdown rendering (FR-209) is a v1.1 concern. Plain text stores identically,
 * so adding it later does not touch the data.
 */
import { useEffect, useRef, useState } from 'react'

export interface NoteFieldProps {
  label: string
  placeholder: string
  value: string | undefined
  onCommit: (value: string | undefined) => void
  debounceMs?: number
}

export function NoteField({
  label,
  placeholder,
  value,
  onCommit,
  debounceMs = 600,
}: NoteFieldProps) {
  const [draft, setDraft] = useState(value ?? '')

  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  /** Set when there is text not yet written. Cleared by every flush. */
  const pending = useRef<string | undefined>(undefined)
  const commit = useRef(onCommit)

  // Kept current in an effect rather than during render. The unmount cleanup
  // below reads it, and effects run in order, so it is always the latest
  // callback by the time the cleanup fires.
  useEffect(() => {
    commit.current = onCommit
  }, [onCommit])

  useEffect(() => {
    return () => {
      if (timer.current !== undefined) clearTimeout(timer.current)
      const next = pending.current
      pending.current = undefined
      if (next !== undefined) commit.current(next === '' ? undefined : next)
    }
  }, [])

  const flush = () => {
    if (timer.current !== undefined) clearTimeout(timer.current)
    timer.current = undefined
    const next = pending.current
    pending.current = undefined
    if (next !== undefined) commit.current(next === '' ? undefined : next)
  }

  const change = (next: string) => {
    setDraft(next)
    pending.current = next
    if (timer.current !== undefined) clearTimeout(timer.current)
    timer.current = setTimeout(flush, debounceMs)
  }

  return (
    <div>
      <label htmlFor="day-note" className="block pb-2 text-sm font-medium">
        {label}
      </label>
      <textarea
        id="day-note"
        value={draft}
        placeholder={placeholder}
        rows={3}
        onChange={(event) => change(event.target.value)}
        onBlur={flush}
        className="w-full resize-y rounded-lg border border-line bg-surface-raised p-2 text-sm transition-all duration-200 hover:border-ink-muted/50 focus:border-accent focus:outline-none"
      />
    </div>
  )
}
